import React from "react";
import { VeriSearchInputProps } from "./VeriSearchInput.types";
import {
  Box,
  CircularProgress,
  Flex,
  Input,
  keyframes,
} from "@chakra-ui/react";
import { CloseIcon, SearchIcon } from "../../../icons";
import { useDebounce } from "use-debounce";

const closeButtonInAnimationKeyFrames = keyframes`
  from {transform: translateY(10px);opacity: 0;}
  to {transform: translateY(0);margin-top: 0;opacity: 1;}
`;

const closeButtonInAnimationAnimation = `${closeButtonInAnimationKeyFrames} .2s ease-in-out`;

export const VeriSearchInput: React.FC<VeriSearchInputProps> = ({
  isOutline,
  label,
  searchValue: userSearchValue,
  inputProps,
  isLoading,
  isSmall,
  onSearchChange,
  searchDelay = 500,
  ...rest
}) => {
  const [searchValue, setSearchValue] = React.useState(userSearchValue || "");
  const [debouncedSearch] = useDebounce(searchValue, searchDelay, {
    leading: searchValue === "",
  });
  const isSearchFilled = !!debouncedSearch;
  const isSearching = isSearchFilled && isLoading;

  React.useEffect(() => {
    setTimeout(() => {
      onSearchChange?.(debouncedSearch);
    }, 300);
  }, [debouncedSearch]);

  return (
    <Box
      borderRadius={6}
      w={"100%"}
      alignItems={"stretch"}
      justifyContent={"space-between"}
      bg={isOutline ? "white" : "bg.grey"}
      border={"1px solid"}
      borderColor={"border.input"}
      {...rest}
    >
      <Flex w={"100%"}>
        <Flex alignItems={"center"} px={4}>
          <SearchIcon boxSize={6} />
        </Flex>
        <Input
          my={0}
          alignSelf={"stretch"}
          fontSize={"26px"}
          outline={"none"}
          display={"block"}
          variant="unstyled"
          width={"100%"}
          placeholder={label}
          onChange={(e) => {
            setSearchValue(e.target.value);
          }}
          value={searchValue}
          autoComplete={"off"}
          minH={"56px"}
          {...(isSmall
            ? {
                fontSize: "16px",
                minH: "40px",
              }
            : undefined)}
          {...inputProps}
        />
        {isSearchFilled && (
          <Box
            minW={isSmall ? undefined : "50px"}
            px={isSmall ? 2 : 4}
            py={isSmall ? 2 : 4}
            _hover={{ cursor: "pointer" }}
            onClick={() => setSearchValue("")}
            animation={
              isSearching ? undefined : closeButtonInAnimationAnimation
            }
          >
            {isSearching ? (
              <CircularProgress
                isIndeterminate
                color={"brand.main"}
                thickness={2}
                mt={0}
                size={"22px"}
              />
            ) : (
              <CloseIcon boxSize={isSmall ? 4 : undefined} />
            )}
          </Box>
        )}
      </Flex>
    </Box>
  );
};
